// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — useActiveDisaster / useActivePADisaster Hooks
//
// useActiveDisaster   — matches global incident → PA disaster.
//   Used ONLY in layout components to drive the shared store value.
//   When an incident is active but no disaster matches, returns null
//   (no silent fallback — the user sees the selector).
//   When no incident is active, falls back to first active/first disaster
//   so the module is usable before linking disasters to incidents.
//
// useActivePADisaster — reads the shared store value set by the layout.
//   Used by all PA/HMGP pages. Reflects both auto-detection AND
//   manual user overrides from the layout disaster selector.
//
// useDisasterFilter   — convenience hook that filters any array using
//   the store value. No changes needed at call sites.
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { usePAGrantsStore } from '../store/paGrantsStore';
import type { PADisaster } from '../types/pagrants';

/**
 * Auto-detects the PA disaster linked to the active global incident.
 * Used in PAGrantsLayout and HMGPLayout to drive setActiveDisasterId.
 *
 * Behaviour:
 *  - activeIncident set   → return matching disaster OR null (no fallback)
 *  - activeIncident null  → fall back to first active / first disaster
 */
export function useActiveDisaster(): PADisaster | null {
  const activeIncident = useAuthStore(s => s.activeIncident);
  const disasters = usePAGrantsStore(s => s.disasters);

  return useMemo(() => {
    if (disasters.length === 0) return null;

    if (activeIncident) {
      // Strict match — no silent fallback when an incident is selected
      return disasters.find(d => d.incidentId && String(d.incidentId) === String(activeIncident.id)) ?? null;
    }

    // No active incident → show data by defaulting to first active disaster
    return disasters.find(d => d.status === 'active') ?? disasters[0] ?? null;
  }, [activeIncident, disasters]);
}

/**
 * Returns the disaster currently selected in the shared PA store.
 * This reflects both auto-detection (via incident match) and manual
 * user overrides from the layout disaster selector dropdown.
 * Use this in all PA/HMGP page components instead of useActiveDisaster.
 */
export function useActivePADisaster(): PADisaster | null {
  const activeDisasterId = usePAGrantsStore(s => s.activeDisasterId);
  const disasters = usePAGrantsStore(s => s.disasters);

  return useMemo(() => {
    if (!activeDisasterId) return null;
    return disasters.find(d => String(d.Rid) === activeDisasterId) ?? null;
  }, [activeDisasterId, disasters]);
}

/** Filter any array by the active PA disaster (reads from store). */
export function useDisasterFilter<T extends { disasterId?: string }>(items: T[]): T[] {
  const activeDisaster = useActivePADisaster();

  return useMemo(() => {
    if (!activeDisaster) return items;
    return items.filter(i => String(i.disasterId) === String(activeDisaster.Rid));
  }, [items, activeDisaster]);
}
